import { Box, Text, Button, Flex } from "@chakra-ui/react";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import React from "react";
import { useTranslation } from "react-i18next";

export interface TourGuideTooltipProps {
  title: string;
  description: string;
  currentStep?: number;
  totalSteps?: number;
  onNext?: () => void;
  onBack?: () => void;
  onClose?: () => void;
  showBack?: boolean;
  showNext?: boolean;
  variant?: "tour" | "hover"; // "tour" shows buttons and progress, "hover" shows only content
}

export const TourGuideTooltip: React.FC<TourGuideTooltipProps> = ({
  title,
  description,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  onClose,
  showBack = false,
  showNext = true,
  variant = "tour",
}) => {
  const { t } = useTranslation("common");
  const isHoverMode = variant === "hover";

  return (
    <Box
      bg="white"
      borderRadius="12px"
      boxShadow="0px 4px 20px rgba(0, 0, 0, 0.15)"
      p={isHoverMode ? "24px" : "16px"}
      minW={isHoverMode ? "400px" : "360px"}
      maxW={isHoverMode ? "500px" : "420px"}
      position="relative"
      zIndex={10000}
    >
      {/* Close button - only in tour mode */}
      {!isHoverMode && onClose && (
        <Box
          position="absolute"
          top="12px"
          right="12px"
          cursor="pointer"
          onClick={onClose}
          fontSize="18px"
          color="#666"
          _hover={{ color: "#000" }}
          transition="color 0.2s"
        >
          ×
        </Box>
      )}

      {/* Title */}
      <Text
        fontSize={isHoverMode ? "20px" : "18px"}
        fontWeight="600"
        color="#1A1A1A"
        mb={isHoverMode ? "12px" : "8px"}
        pr={!isHoverMode && onClose ? "28px" : "0"}
      >
        {title}
      </Text>

      {/* Description */}
      <Text
        fontSize={isHoverMode ? "14px" : "13px"}
        color="#404040"
        lineHeight="1.6"
        mb={isHoverMode ? "0" : "16px"}
      >
        {description}
      </Text>

      {/* Footer with progress and buttons - only in tour mode */}
      {!isHoverMode && (
        <Flex justifyContent="space-between" alignItems="center" gap="12px">
          {/* Progress indicator */}
          {currentStep && totalSteps && (
            <Text fontSize="12px" color="#757575">
              {t("tourGuide.progress", {
                current: currentStep,
                total: totalSteps,
              }) || `${currentStep} of ${totalSteps}`}
            </Text>
          )}

          {/* Navigation buttons */}
          <Flex gap="6px">
            {showBack && onBack && (
              <Button
                variant="ghost"
                leftIcon={<ChevronLeftIcon />}
                onClick={onBack}
                fontSize="12px"
                height="32px"
                px="8px"
                minW="auto"
                color="#1A1A1A"
                width={"100px"}
                _hover={{ bg: "#F5F5F5" }}
              >
                {t("tourGuide.back") || "Back"}
              </Button>
            )}
            {showNext && onNext && (
              <Button
                bg="#1A1A1A"
                color="white"
                onClick={onNext}
                fontSize="12px"
                height="32px"
                px="12px"
                minW="auto"
                _hover={{ bg: "#2f2d2d" }}
                width={"100px"}
              >
                {currentStep && totalSteps && currentStep === totalSteps
                  ? t("finish") || "Finish"
                  : t("tourGuide.next") || "Next"}
              </Button>
            )}
          </Flex>
        </Flex>
      )}
    </Box>
  );
};
